export interface PatientSearchCriteria {
  docType: string;
  docNumber: string;
  country: string;
  name: string;
  otherNames: string;
  lastName: string;
  otherLastNames: string;
  birthDate: string;
  gender: string;
}

export interface AuditSearchCriteria {
  status: boolean;
  statusValue: number | '';
  duplicates: boolean;
  nnPatients: boolean;
  deceasedPatients: boolean;
}

export type MpiSearchMode = 'search' | 'audit' | null;

export type MpiPatientDTO = {
  id: number;
  mpi_name: string;
  other_names: string | null;
  mpi_surname: string;
  other_surnames: string | null;
  id_identification_type: number | null;
  identification_number: string | null;
  id_identification_country: number | null;
  date_of_birth: string | null;
  id_gender: number | null;
  id_fhir: string;
  active: boolean;
  id_status: number;
  mail: string | null;
  image_url?: string | null;
  deceasedDateTime?: string | null;
  sys_creation_date: string;
  sys_update_date: string;
  sys_created_by: string;
  sys_updated_by: string;
}

export interface SessionObservation {
  text: string;
  professional_name: string;
  observation_date: string;
}

export interface CheckAliveData {
  deceased: boolean;
  deceasedDateTime?: string;
}

export interface SessionObservationsPage {
  session_observations: SessionObservation[];
  pagination: PaginationInfo;
}

export type MpiPatientDetailDTO = MpiPatientDTO & {
  deceased_date?: string | null;
  session_observations?: SessionObservation[] | null;
};

export const MPI_STATUS_MAP: Record<number, string> = {
  1: "Permanente",
  2: "Validado",
  3: "Temporal",
};

// Pagination info for both MpiPaginatedResponse and MpiCoincidenceResponse
export interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  limit: number;
  totalPages: number;
}

export interface MpiPaginatedResponse {
  message: string;
  data: {
    mpi: MpiPatientDTO[];
    pagination: PaginationInfo;
  }
}

export type MpiDuplicatePatientDTO = Omit<MpiPatientDTO, 'identification_number'> & {
  id_identification_number: string | null;
}

export interface MpiDuplicateGroup {
  group_id: string;
  patient_ids: number[];
  patients: MpiDuplicatePatientDTO[];
  matching_info: {
    match_criteria: string;
  };
  latest_creation_date: string;
}

export interface MpiDuplicatesResponse {
  message: string;
  data: MpiDuplicateGroup[];
  pagination: PaginationInfo;
}

export type DuplicateRow = MpiDuplicatePatientDTO & {
  _groupId: string;
  _isFirstInGroup: boolean;
  _isLastInGroup: boolean;
  _group: MpiDuplicateGroup;
};

export interface ValidationStatus {
  id: number;
  validation_status: string;
  active: boolean;
}

export interface ValidationStatusResponse {
  message: string;
  data: {
    validation_status: ValidationStatus[];
  };
}

export type MpiCoincidenceDTO = {
  // --- Datos base del paciente (BackendPatient) ---
  id: number;
  mpi_name: string;
  other_names: string | null;
  mpi_surname: string;
  other_surnames: string | null;
  id_identification_type: number;
  identification_number: string;
  id_identification_country: number;
  date_of_birth: string;
  id_gender: number;
  id_fhir: string;
  active: boolean;
  id_status: number;              // 1=Permanente, 2=Validado, 3=Temporal
  mail: string | null;

  // --- Campos de auditoría (metadata) ---
  sys_creation_date: string;
  sys_update_date: string;
  sys_created_by: string;
  sys_updated_by: string;

  // --- Campos EXCLUSIVOS de getMpiCoincidences (scoring) ---
  mpi_name_similarity: number;
  mpi_surname_similarity: number;
  date_of_birth_similarity: number;
  weight: number;
}

export interface MpiCoincidenceResponse {
  message: string;
  data: {
    mpi: MpiCoincidenceDTO[];
    pagination: {
      total: number;
      page: number;
      totalPages: number;
      pageSize: number;
      limit: number;
    };
  };
}

export interface MpiCoincidencesQueryParams {
  page: number;
  limit: number;
  mpi_name?: string;
  other_names?: string;
  mpi_surname?: string;
  other_surnames?: string;
  id_identification_type?: number;
  id_identification_country?: number;
  identification_number?: string;
  id_gender?: number;
  date_of_birth?: string;
}

export const INITIAL_PATIENT_CRITERIA: PatientSearchCriteria = {
  docType: '',
  docNumber: '',
  country: '',
  name: '',
  otherNames: '',
  lastName: '',
  otherLastNames: '',
  birthDate: '',
  gender: '',
};

export const INITIAL_AUDIT_CRITERIA: AuditSearchCriteria = {
  status: false,
  statusValue: '',
  duplicates: false,
  nnPatients: false,
  deceasedPatients: false,
};

// Datos del formulario de Creación / Alta de paciente
export interface CreatePatientFormValues {
  avatar?: File | null;
  docType: string;
  docNumber: string;
  country: string;
  name: string;
  otherNames?: string;
  lastName: string;
  otherLastNames?: string;
  birthDate: string;
  age: string;
  gender: string;
  email?: string;
}

export interface CreateMpiPatientResponse {
  message: string;
  data: {
    id: number;
    mpi_name: string;
    other_names: string | null;
    mpi_surname: string;
    other_surnames: string | null;
    id_identification_type: string;
    identification_number: string;
    id_identification_country: number;
    date_of_birth: string;
    id_gender: string;
    id_fhir: string;
    active: boolean;
    id_status: number;
    mail: string | null;
    sys_creation_date: string;
    sys_update_date: string;
    sys_created_by: string;
    sys_updated_by: string;
    image_url: string | null;
  };
}

export interface UpdateTemporalPatientResponse {
  message: string;
  data: MpiPatientDTO;
}

export interface UpdateValidatedPatientResponse {
  message: string;
  data: MpiPatientDTO;
}

export interface MpiBackendError {
  message: string;
  error_code?: string;
  errors?: Record<string, string>[];
}


export const INITIAL_CREATE_PATIENT_VALUES: CreatePatientFormValues = {
  avatar: null,
  docType: '',
  docNumber: '',
  country: '',
  name: '',
  otherNames: '',
  lastName: '',
  otherLastNames: '',
  birthDate: '',
  age: '',
  gender: '',
  email: '',
};
